import { useTemplates, useDeleteTemplate } from '../../hooks/useTemplates';
import { Loader2, AlertCircle, CheckCircle, Plus, Edit, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import type { ProviderTemplate } from '../../types';
import { getProviderLogo } from '../../utils/providerLogos';

interface TemplateSelectorProps {
  onSelect?: (templateId: string) => void;
}

export const TemplateSelector = ({ onSelect }: TemplateSelectorProps) => {
  const { data: templates, isLoading, error } = useTemplates();
  const deleteTemplate = useDeleteTemplate();
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const handleDelete = async (templateId: string) => {
    if (!confirm('Are you sure you want to delete this template? Providers using this template will need to be reconfigured.')) {
      return;
    }

    try {
      setDeletingId(templateId);
      await deleteTemplate.mutateAsync(templateId);
    } catch (error) {
      console.error('Failed to delete template:', error);
    } finally {
      setDeletingId(null);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Error Loading Templates</h3>
          <p className="text-gray-600">{(error as any).message || 'Failed to load templates'}</p>
        </div>
      </div>
    );
  }

  if (!templates || templates.length === 0) {
    return (
      <div className="text-center py-12">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">No Templates Available</h3>
        <p className="text-gray-600">No provider templates are configured.</p>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Provider Templates</h2>
          <p className="text-gray-600 mt-1">
            Manage OAuth provider templates
          </p>
        </div>
        <Link to="/templates/new" className="btn btn-primary px-4 py-2 flex items-center space-x-2">
          <Plus className="h-4 w-4" />
          <span>Add Template</span>
        </Link>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {templates.map((template: ProviderTemplate) => (
          <div
            key={template.id}
            className="card p-6 hover:shadow-lg transition-shadow"
          >
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center space-x-3">
                {getProviderLogo(template.providerType, 'md')}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">{template.name}</h3>
                  <p className="text-sm text-gray-500">{template.providerType}</p>
                </div>
              </div>
              {template.isActive && (
                <CheckCircle className="h-5 w-5 text-green-500" />
              )}
            </div>

            <div className="space-y-2 text-sm text-gray-600">
              <div>
                <span className="font-medium">Default Scopes:</span>
                <div className="mt-1">
                  {Array.isArray(template.defaultScopes) ? (
                    template.defaultScopes.map((scope, index) => (
                      <span
                        key={index}
                        className="inline-block bg-gray-100 rounded px-2 py-0.5 mr-1 mb-1 text-xs"
                      >
                        {scope}
                      </span>
                    ))
                  ) : (
                    <span className="inline-block bg-gray-100 rounded px-2 py-0.5 mr-1 mb-1 text-xs">
                      {template.defaultScopes || 'None'}
                    </span>
                  )}
                </div>
              </div>

              <div>
                <span className="font-medium">PKCE:</span>{' '}
                {template.requiresPkce ? 'Required' : 'Optional'}
              </div>

              <div>
                <span className="font-medium">Auth Method:</span> {template.tokenEndpointAuthMethod}
              </div>
            </div>

            <div className="flex space-x-2 mt-4 pt-4 border-t border-gray-200">
              {onSelect && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onSelect(template.id);
                  }}
                  className="btn btn-primary px-4 py-2 flex items-center space-x-2 flex-1"
                >
                  <span>Use Template</span>
                </button>
              )}

              <Link
                to={`/templates/${template.id}/edit`}
                className="btn btn-outline px-4 py-2 flex items-center space-x-2"
                onClick={(e) => e.stopPropagation()}
              >
                <Edit className="h-4 w-4" />
              </Link>

              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleDelete(template.id);
                }}
                disabled={deletingId === template.id}
                className="btn btn-outline px-4 py-2 flex items-center space-x-2 text-red-600 border-red-300 hover:bg-red-50"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
